/**
 * useWalletDecisions — data hook powering the "My Decisions" screens.
 *
 * Reads the device-scoped decision list from
 *   GET  /api/wallet/decisions?device_id=…
 * Refreshes on window focus and every 45s while mounted.
 *
 * Also exposes small imperative helpers:
 *   sendDemo()          → POST /api/admin/decisions/demo-send
 *   resetDemo()         → POST /api/admin/decisions/demo-reset
 *   fetchDecision(id)   → GET  /api/wallet/decisions/{id}
 *   acknowledgeTrust(id)  → POST /api/wallet/decisions/{id}/trust/acknowledge
 *   fetchUnderstand(id)   → GET  /api/wallet/decisions/{id}/understand
 *   attestUnderstand(id)  → POST /api/wallet/decisions/{id}/understand/attest
 *   fetchCheck(id)        → GET  /api/wallet/decisions/{id}/check
 *   reviewCheck(id)       → POST /api/wallet/decisions/{id}/check/review
 *   recordAct(id, choice) → POST /api/wallet/decisions/{id}/act
 *   fetchCertificate(id)  → GET  /api/wallet/decisions/{id}/certificate
 */
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { loadWallet } from "@/lib/wallet-storage";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const POLL_MS = 45_000;

export const DECISION_STATUS = {
  TRUST:      "TRUST_PENDING",
  UNDERSTAND: "UNDERSTAND_PENDING",
  CHECK:      "CHECK_PENDING",
  ACT:        "ACT_PENDING",
  MORE_INFO:  "MORE_INFORMATION_REQUESTED",
  COMPLETED:  "COMPLETED",
};

export const CONSUMER_RESPONSE = {
  READY:     "READY_TO_PROCEED",
  MORE_INFO: "MORE_INFORMATION_REQUESTED",
  DO_NOT:    "DO_NOT_PROCEED",
};

export const MORE_INFO_TOPICS = [
  { id: "FEES",                 label: "Fees" },
  { id: "RISKS",                label: "Risks" },
  { id: "ACCESS_TO_MONEY",      label: "Access to my money" },
  { id: "FINANCIAL_COMMITMENT", label: "Financial commitment" },
  { id: "SOMETHING_ELSE",       label: "Something else" },
];

export const DO_NOT_PROCEED_REASONS = [
  { id: "FINANCIAL_COMMITMENT",     label: "Financial commitment" },
  { id: "PRODUCT_NOT_RIGHT_FOR_ME", label: "Product isn't right for me" },
  { id: "NEED_TO_RECONSIDER",       label: "I need to reconsider my plans" },
  { id: "CONCERNS_ABOUT_PROPOSAL",  label: "I have concerns about the proposal" },
  { id: "PREFER_NOT_TO_SAY",        label: "Prefer not to say" },
];

export function useWalletDecisions() {
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;
  const [data, setData] = useState({ active: [], completed: [], total: 0, loading: true });

  const refresh = useCallback(async () => {
    if (!deviceId) { setData({ active: [], completed: [], total: 0, loading: false }); return; }
    try {
      const { data: d } = await axios.get(`${API}/wallet/decisions`, { params: { device_id: deviceId } });
      setData({ ...d, loading: false });
    } catch {
      setData(prev => ({ ...prev, loading: false }));
    }
  }, [deviceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => { await refresh(); if (cancelled) return; })();
    const iv = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("altara.decisions.refresh", refresh);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("altara.decisions.refresh", refresh);
    };
  }, [refresh]);

  const trustPending = useMemo(
    () => data.active.filter(d => d.status === DECISION_STATUS.TRUST),
    [data.active],
  );

  return { ...data, deviceId, refresh, trustPending };
}

export async function fetchDecision(decisionId, deviceId) {
  const { data } = await axios.get(`${API}/wallet/decisions/${decisionId}`, { params: { device_id: deviceId } });
  return data;
}

export async function acknowledgeTrust(decisionId, deviceId, extra = {}) {
  const { data } = await axios.post(`${API}/wallet/decisions/${decisionId}/trust/acknowledge`, {
    device_id: deviceId,
    proposal_provenance_ok:  true,
    adviser_identified_ok:   true,
    institution_verified_ok: true,
    ...extra,
  });
  window.dispatchEvent(new Event("altara.decisions.refresh"));
  return data;
}

export async function fetchUnderstand(decisionId, deviceId) {
  const { data } = await axios.get(`${API}/wallet/decisions/${decisionId}/understand`, { params: { device_id: deviceId } });
  return data;
}

export async function attestUnderstand(decisionId, deviceId, attestedIds, savedQuestions = []) {
  const { data } = await axios.post(`${API}/wallet/decisions/${decisionId}/understand/attest`, {
    device_id: deviceId,
    attested_ids: attestedIds,
    saved_questions: savedQuestions,
  });
  window.dispatchEvent(new Event("altara.decisions.refresh"));
  return data;
}

export async function fetchCheck(decisionId, deviceId) {
  const { data } = await axios.get(`${API}/wallet/decisions/${decisionId}/check`, { params: { device_id: deviceId } });
  return data;
}

export async function reviewCheck(decisionId, deviceId, consumerNote) {
  const { data } = await axios.post(`${API}/wallet/decisions/${decisionId}/check/review`, {
    device_id: deviceId,
    consumer_note: consumerNote,
  });
  window.dispatchEvent(new Event("altara.decisions.refresh"));
  return data;
}

export async function recordAct(decisionId, deviceId, payload) {
  const { data } = await axios.post(`${API}/wallet/decisions/${decisionId}/act`, {
    device_id: deviceId,
    ...payload,
  });
  window.dispatchEvent(new Event("altara.decisions.refresh"));
  return data;
}

export async function fetchOutcomeEvidence(decisionId, deviceId) {
  const { data } = await axios.get(`${API}/wallet/decisions/${decisionId}/outcome-evidence`, { params: { device_id: deviceId } });
  return data;
}

export async function sendDemoDecision(deviceId) {
  const { data } = await axios.post(`${API}/admin/decisions/demo-send`, { device_id: deviceId });
  window.dispatchEvent(new Event("altara.decisions.refresh"));
  return data;
}

export async function resetDemoDecisions(deviceId) {
  const { data } = await axios.post(`${API}/admin/decisions/demo-reset`, { device_id: deviceId });
  window.dispatchEvent(new Event("altara.decisions.refresh"));
  return data;
}

/** Human-friendly label for a decision status. */
export function statusLabel(status) {
  switch (status) {
    case DECISION_STATUS.TRUST:      return "Trust check";
    case DECISION_STATUS.UNDERSTAND: return "Understand";
    case DECISION_STATUS.CHECK:      return "Check";
    case DECISION_STATUS.ACT:        return "Act";
    case DECISION_STATUS.MORE_INFO:  return "More information requested";
    case DECISION_STATUS.COMPLETED:  return "Completed";
    default: return status || "";
  }
}

/** ZAR formatter — R 250,000. */
export function zar(n) {
  if (n === null || n === undefined || Number.isNaN(+n)) return "—";
  return "R " + (+n).toLocaleString("en-ZA", { maximumFractionDigits: 0 });
}
